import { useEffect } from 'react';
import { RemoteImage } from '@banhala/ui-kit';
import Animated, {
  cancelAnimation,
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withDelay,
  withRepeat,
  withSequence,
  Easing,
} from 'react-native-reanimated';
import type { HomeTabOverlayAnimation } from 'shared/src/api/homeService/getHomeTabs/types';

interface TabOverlayImageProps {
  uri: string;
  width: number;
  height: number;
  animation: HomeTabOverlayAnimation | null;
}

export const TabOverlayImage = ({
  uri,
  width,
  height,
  animation,
}: TabOverlayImageProps) => {
  const rotateY = useSharedValue(0);

  useEffect(() => {
    if (!animation || animation.type !== 'SHAKE') {
      return;
    }

    const { duration, interval, repeat_count } = animation;
    const flipDuration = (duration * 1000) / 2;

    // interval 후에 시작
    const startDelay = interval * 1000;

    // 0 → 360 Y축 회전
    rotateY.value = withDelay(
      startDelay,
      withRepeat(
        withSequence(
          withTiming(180, {
            duration: flipDuration,
            easing: Easing.inOut(Easing.ease),
          }),
          withTiming(360, {
            duration: flipDuration,
            easing: Easing.inOut(Easing.ease),
          }),
          // interval 대기
          withDelay(interval * 1000, withTiming(360, { duration: 0 })),
        ),
        repeat_count,
        false,
      ),
    );
    return () => cancelAnimation(rotateY);
  }, [animation, rotateY]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      { perspective: 800 },
      { rotateY: `${rotateY.value}deg` },
    ],
  }));

  if (!animation) {
    return <RemoteImage uri={uri} width={width} height={height} />;
  }

  return (
    <Animated.View style={animatedStyle}>
      <RemoteImage uri={uri} width={width} height={height} />
    </Animated.View>
  );
};
